import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState, } from "react";
import { FiPlay } from "@react-icons/all-files/fi/FiPlay";
import { IconPin } from "components/Icon";
import { CampaignUpdateStatus } from "types/Campaign/CampaignUpdate";
import "./_style.scss";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import useGetVideoThumb from "components/VideoAsset/useGetVideoThumb";
import CampaignUpdateModal from "../CampaignUpdateModal/CampaignUpdateModal";
function getPlainText(html) {
    var _a;
    const withSpacedBreaks = html.replace(/<\/(p|div|li|h[1-6]|blockquote)>|<br\b[^>]*>/gi, " ");
    const doc = new DOMParser().parseFromString(withSpacedBreaks, "text/html");
    return ((_a = doc.body.textContent) !== null && _a !== void 0 ? _a : "").replace(/\s+/g, " ").trim();
}
function CampaignUpdateTile({ update, campaign, isCommentsEnabled }) {
    var _a, _b, _c;
    const { title, content, updateType, assets, playbackId, thumbnailToken, } = update;
    const { translateChoice: tChoice } = useLanguageProvider();
    const [reactions, setReactions] = useState(update.reactions);
    const [commentsCount, setCommentsCount] = useState(update.commentsCount);
    const [modalOpen, setModalOpen] = useState(false);
    const isMounted = useRef(false);
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        const basePath = window.location.pathname
            .replace(/\/updates\/[^/]+$/, "")
            .replace(/\/$/, "");
        if (modalOpen) {
            window.history.replaceState(null, "", `${basePath}/updates/${update.id}`);
        }
        else {
            window.history.replaceState(null, "", basePath || "/");
        }
    }, [modalOpen, update.id]);
    const isText = updateType === "Text";
    const isImage = updateType === "Image";
    const isVideo = updateType === "Video";
    const isPinned = (_a = update.isPinned) !== null && _a !== void 0 ? _a : false;
    const assetThumb = useGetVideoThumb((_c = (_b = assets[0]) === null || _b === void 0 ? void 0 : _b.publicUrl) !== null && _c !== void 0 ? _c : "");
    const totalReactions = useMemo(() => reactions.reduce((sum, r) => sum + r.count, 0), [reactions]);
    const excerpt = useMemo(() => getPlainText(content), [content]);
    const backgroundStyle = useMemo(() => {
        var _a, _b;
        if (isImage && assets[0]) {
            return { backgroundImage: `url(${(_a = assets[0].publicUrl) !== null && _a !== void 0 ? _a : assets[0].url})` };
        }
        if (isVideo) {
            if (update.status === CampaignUpdateStatus.Pending) {
                const assets = (_b = campaign.assets) !== null && _b !== void 0 ? _b : [];
                return {
                    backgroundImage: assets.length > 0 ? `url(${assets[0].publicUrl})` : "",
                };
            }
            if (playbackId && thumbnailToken) {
                return {
                    backgroundImage: `url(https://image.mux.com/${playbackId}/thumbnail.png?token=${thumbnailToken})`,
                };
            }
            if (assets[0]) {
                return {
                    backgroundImage: `url(${assetThumb})`,
                };
            }
        }
        return undefined;
    }, [
        isImage,
        isVideo,
        assets,
        playbackId,
        thumbnailToken,
        assetThumb,
        campaign.assets,
        update.status,
    ]);
    const tileClassName = [
        "campaign-updates__tile",
        `--${updateType.toLowerCase()}`,
        isPinned ? "--pinned" : "",
    ].filter(Boolean).join(" ");
    const handleClose = () => setModalOpen(false);
    return (_jsxs(_Fragment, { children: [modalOpen && (_jsx(CampaignUpdateModal, { update: { ...update, reactions, commentsCount }, campaign: campaign, onReactionsChange: setReactions, onCommentAdded: () => setCommentsCount((c) => c + 1), onClose: handleClose, isCommentsEnabled: isCommentsEnabled })), _jsxs("div", { className: tileClassName, style: backgroundStyle, onClick: () => setModalOpen(true), onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        setModalOpen(true);
                    }
                }, role: "button", tabIndex: 0, "aria-label": `View update: ${title}`, children: [isPinned && (_jsxs("div", { className: "campaign-updates__tile-pin-badge", children: [_jsx(IconPin, { "aria-hidden": "true" }), "Pinned"] })), _jsxs("div", { className: "campaign-updates__tile-badge-container", children: [update.status === CampaignUpdateStatus.Pending && (_jsx("div", { className: "campaign-updates__tile-type-badge --processing", children: "Processing" })), _jsx("div", { className: "campaign-updates__tile-type-badge", children: !isImage ? updateType : `${assets.length} ${tChoice("Photo|Photos", assets.length)}` })] }), isVideo && (_jsx("div", { className: "campaign-updates__tile-play", "aria-hidden": "true", children: _jsx(FiPlay, {}) })), _jsxs("div", { className: `campaign-updates__tile-overlay${isText ? " --text" : ""}`, children: [_jsxs("div", { className: "campaign-updates__tile-overlay-content", children: [_jsx("div", { className: "campaign-updates__tile-overlay-title", children: title }), isText && (_jsx("div", { className: "campaign-updates__tile-overlay-excerpt", children: excerpt }))] }), (commentsCount > 0 || totalReactions > 0) && (_jsxs("div", { className: "campaign-updates__tile-overlay-meta", children: [isCommentsEnabled && commentsCount > 0 && (_jsx("p", { children: `${commentsCount} ${tChoice("Comment|Comments", commentsCount)}` })), totalReactions > 0 && (_jsx("p", { children: `${totalReactions} ${tChoice("Reaction|Reactions", totalReactions)}` }))] }))] })] })] }));
}
export default CampaignUpdateTile;
